import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ChangeDetectorRef, HostBinding } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { User } from './Models/User';
import { UserService } from './services/user.service';
import { NavigationGuardService } from './services/navigation-guard.service';
import { VersionCheckService } from './services/version-check.service';
import { AboutDialogComponent } from './about-dialog/about-dialog.component';
import { ProbateListFilterStorageService } from './services/probate-list-filter-storage.service';



@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: false
})

export class AppComponent implements OnInit {

  public shownScreen: string = "";

  public hasLoggedInUser = false;
  public loggedInUser = <User | undefined>{};
  public isAdminMenuOpen = false;
  public isFinanceMenuOpen = false;
  public isReportsMenuOpen = false;
  public isPricingMenuOpen = false;
  public isSupervisorMenuOpen = false;
  public isHelpMenuOpen = false;
  public isClientPortalRoute = false;
  public isPaygPaymentRoute = false;
  public updateRequired = false;
  @HostBinding('class.portal-route') get portalRouteClass() { return this.isClientPortalRoute; }

  constructor(
    private http: HttpClient,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private navGuard: NavigationGuardService,
    private versionCheck: VersionCheckService,
    private dialog: MatDialog,
    private probateListFilterStorage: ProbateListFilterStorageService
  ) { }

  ngOnInit() {
    this.updateRouteState(window.location.pathname);

    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe((e: any) => {
        this.updateRouteState(e.urlAfterRedirects);
      });

    this.versionCheck.updateRequired$.subscribe(() => {
      this.updateRequired = true;
    });
  }

  refreshApp() {
    window.location.reload();
  }

  private updateRouteState(url: string) {
    this.isClientPortalRoute = url.startsWith('/client');

    this.isPaygPaymentRoute =
      url.startsWith('/admin-payg/payment-success') ||
      url.startsWith('/admin-payg/payment-failed');
  }

  openAboutDialog() {
      this.dialog.open(AboutDialogComponent, { width: '480px' });
  }

  logout() {
    this.navGuard.requestAction(() => this.doLogout());
  }

  private doLogout() {
    this.loggedInUser = undefined;
    this.hasLoggedInUser = false;
    this.userService.clearUser();
    // A stale probate-list filter/search left over from this (or a previous) user on this
    // browser is exactly what makes "my probates are missing" so hard to spot - see
    // ProbateListFilterStorageService. Resetting on both logout and the next login means
    // whoever signs in next always starts from a clean list.
    this.probateListFilterStorage.requestReset();
    this.router.navigate(['/']);
  }

  isSystemAdministrator() {
    return this.userHasRoleWithId(1);
  }

  isSupervisor() {
    return this.userHasRoleWithId(2);
  }

  isFinance() {
    return this.userHasRoleWithId(3);
  }

  getUserInitials(): string {
    if (!this.loggedInUser) return '?';
    const first = this.loggedInUser.firstName?.charAt(0)?.toUpperCase() || '';
    const last = this.loggedInUser.lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  }

  userHasRoleWithId(id: number) {

    if (this.loggedInUser != undefined && this.loggedInUser.roles) {
      // Check if the user has a role with the specified ID
      if (this.loggedInUser.roles.some(role => role.id === id)) {
        return true;
      }
      else {
        return false;
      }
    }
    else {
      return false;
    }
  }



  userLoggedInOrOutStatusChange(user: User | undefined) {

    this.loggedInUser = user;
    this.hasLoggedInUser = (user != undefined);

    // A fresh login - see the matching comment in doLogout() for why this is reset here too.
    this.probateListFilterStorage.requestReset();

    // Store user in service for access by child components
    this.userService.setLoggedInUser(user);
    
    // Trigger change detection to ensure UI updates properly
    this.cdr.detectChanges();

  }

  navigateToScreen(screen: string) {
    this.navGuard.requestAction(() => this.doNavigateToScreen(screen));
  }

  private doNavigateToScreen(screen: string) {
    switch(screen) {
      case 'Home':
        this.router.navigate(['/'], { state: { user: this.loggedInUser } });
        break;
      case 'Reading':
        this.router.navigate(['/reading', '1656068303920013'], { state: { user: this.loggedInUser }});
        break;
      case 'Reporting':
        this.router.navigate(['/reporting', '1707316837118821'], { state: { user: this.loggedInUser } });
        break;
      case 'ProbateList':
        // Deliberately pressing this nav button (as opposed to the browser's back button, or
        // the reporting screen's own "back to list" arrow - see ReportingComponent.goBackToList,
        // which navigates to the same route without resetting anything) is treated as a fresh
        // start every time, even if already on the probate list screen (onSameUrlNavigation:
        // 'reload' + NoReuseStrategy mean that still destroys and recreates the component) - any
        // status/user/search filter left over from before is wiped so a probate that's merely
        // filtered out can't masquerade as "missing".
        this.probateListFilterStorage.requestReset();
        this.router.navigate(['/probate-list'], { state: { user: this.loggedInUser } });
        break;
      case 'Feedback':
        this.router.navigate(['/feedback']);
        break;
      case 'CharityDatabase':
        this.router.navigate(['/charity-database'], { state: { user: this.loggedInUser } });
        break;
      case 'PAFMatching':
        // TODO: Add route when component is ready
        break;
      case 'AdminUsers':
        this.router.navigate(['/admin-users']);
        break;
      case 'AdminClients':
        this.router.navigate(['/admin-clients'], { state: { user: this.loggedInUser } });
        break;
      case 'AdminBeneficiaries':
        this.router.navigate(['/admin-beneficiaries']);
        break;
      case 'AdminUtilities':
        this.router.navigate(['/admin-utilities']);
        break;
      case 'AdminTools':
        this.router.navigate(['/admin-tools']);
        break;
      case 'AdminPayg':
        this.router.navigate(['/admin-payg']);
        break;
      case 'AdminPricing':
        this.router.navigate(['/admin-pricing']);
        break;
      case 'AdminClientTier':
        this.router.navigate(['/admin-client-tier']);
        break;
      case 'HmrcWills':
        this.router.navigate(['/reports/hmrc-wills'], { state: { user: this.loggedInUser } });
        break;
      case 'InvoiceReport':
        this.router.navigate(['/finance/invoice-report']);
        break;
      case 'ProbateStatusReport':
        this.router.navigate(['/reports/probate-status']);
        break;
      case 'Productivity':
        this.router.navigate(['/reports/productivity']);
        break;
      case 'Allocation':
        this.router.navigate(['/allocation']);
        break;
      default:
        this.router.navigate(['/']);
    }
  }

  title = 'charitycentral.client';
}


